import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import { formatarCNPJ, toApiDate } from '@spark/utils/formatters';
import {
  getDepositoTabelaPrecoById,
  createDepositoTabelaPreco,
  updateDepositoTabelaPreco,
  applyDepositoTabelaPreco,
  getDepositoTabelaPrecoClienteById,
  createDepositoTabelaPrecoCliente,
  updateDepositoTabelaPrecoCliente,
  deleteDepositoTabelaPrecoCliente,
  getNomeClientePorCnpj
} from '@spark/services/deposito';
import { tabelaPrecoFilterConfig } from '../config/tabelaPrecoFilterConfig';
import { getTabelaPrecoColumns } from '../config/tabelaPrecoColumns';
import { tabelaPrecoFormStructure, tabelaPrecoFormSections, validateTabelaPrecoForm } from '../config/tabelaPrecoFormConfig';
import { tabelaPrecoClienteFilterConfig } from '../config/tabelaPrecoClienteFilterConfig';
import { getTabelaPrecoClienteColumns } from '../config/tabelaPrecoClienteColumns';
import {
  emptyTabelaPrecoClienteForm,
  tabelaPrecoClienteFormSections,
  tabelaPrecoClienteFormStructure
} from '../config/tabelaPrecoClienteFormConfig';
import { useDepositoTabelaPrecoSelection } from '../hooks/useDepositoTabelaPrecoSelection';
import { useDepositoTabelaPrecoClientes } from '../hooks/useDepositoTabelaPrecoClientes';

const toRowModel = (item) => ({
  id: item.id,
  nomeTabelaPrecoDeposito: item.nomeTabelaPrecoDeposito,
  nomeResponsavel: item.nomeResponsavel,
  valorUnPallet: item.valorUnPallet,
  valorUnEtiqueta: item.valorUnEtiqueta,
  valorUnArmazenagemPallet: item.valorUnArmazenagemPallet,
  freetime: item.freetime,
  cobrarPaletizacaoRetrabalho: item.cobrarPaletizacaoRetrabalho === true,
  cobrarEtiquetagemRetrabalho: item.cobrarEtiquetagemRetrabalho === true,
  dataVigenciaInicio: item.dataVigenciaInicio,
  dataVigenciaFim: item.dataVigenciaFim
});

const toFormDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDigitsOnly = (value) => (value ? String(value).replace(/\D/g, '') : undefined);

const emptyFormValues = {
  id: '',
  nomeTabelaPrecoDeposito: '',
  nomeResponsavel: '',
  valorUnPallet: '',
  valorUnEtiqueta: '',
  valorUnArmazenagemPallet: '',
  freetime: '',
  dataVigenciaInicio: '',
  dataVigenciaFim: '',
  observacoes: '',
  cobrarPaletizacaoRetrabalho: false,
  cobrarEtiquetagemRetrabalho: false
};

const mapApiToFormValues = (data) => ({
  id: data?.id ?? '',
  nomeTabelaPrecoDeposito: data?.nomeTabelaPrecoDeposito || '',
  nomeResponsavel: data?.nomeResponsavel || '',
  valorUnPallet: data?.valorUnPallet ?? '',
  valorUnEtiqueta: data?.valorUnEtiqueta ?? '',
  valorUnArmazenagemPallet: data?.valorUnArmazenagemPallet ?? '',
  freetime: data?.freetime ?? '',
  dataVigenciaInicio: data?.dataVigenciaInicio ? toFormDate(data.dataVigenciaInicio) : '',
  dataVigenciaFim: data?.dataVigenciaFim ? toFormDate(data.dataVigenciaFim) : '',
  observacoes: data?.observacoes || '',
  cobrarPaletizacaoRetrabalho: data?.cobrarPaletizacaoRetrabalho === true,
  cobrarEtiquetagemRetrabalho: data?.cobrarEtiquetagemRetrabalho === true
});

const buildPayload = (values) => {
  const payload = {
    NomeTabelaPrecoDeposito: normalizeText(values.nomeTabelaPrecoDeposito),
    NomeResponsavel: normalizeText(values.nomeResponsavel),
    ValorUnPallet: parseNumber(values.valorUnPallet),
    ValorUnEtiqueta: parseNumber(values.valorUnEtiqueta),
    ValorUnArmazenagemPallet: parseNumber(values.valorUnArmazenagemPallet),
    Freetime: parseNumber(values.freetime),
    DataVigenciaInicio: values.dataVigenciaInicio ? toApiDate(values.dataVigenciaInicio) : null,
    DataVigenciaFim: values.dataVigenciaFim ? toApiDate(values.dataVigenciaFim) : null,
    Observacoes: normalizeText(values.observacoes),
    CobrarPaletizacaoRetrabalho: values.cobrarPaletizacaoRetrabalho === true,
    CobrarEtiquetagemRetrabalho: values.cobrarEtiquetagemRetrabalho === true
  };

  return payload;
};

const mapClienteApiToFormValues = (data) => ({
  id: data?.id ?? '',
  nome: data?.nome || '',
  cnpjCliente: formatarCNPJ(data?.cnpjCliente || ''),
  tipoOperacao: data?.tipoOperacao || '',
  idTabelaPrecoDeposito: data?.idTabelaPrecoDeposito ?? ''
});

const buildClientePayload = (values, tabelaPrecoId, isUpdate) => {
  const nome = normalizeText(values.nome);
  const tipoOperacao = normalizeText(values.tipoOperacao);
  const payload = {
    IdTabelaPrecoDeposito: tabelaPrecoId,
    Nome: isUpdate ? (nome ?? '') : nome,
    CNPJCliente: toDigitsOnly(values.cnpjCliente),
    TipoOperacao: isUpdate ? (tipoOperacao ?? '') : tipoOperacao
  };

  if (isUpdate) {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined)
    );
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
};

const DepositoTabelaPrecoSelectionModal = ({ show, onHide, onSelect, onSelectComplete }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyFormValues);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [clienteEditingId, setClienteEditingId] = useState(null);
  const [clienteFormInitialValues, setClienteFormInitialValues] = useState(emptyTabelaPrecoClienteForm);
  const [clienteSaving, setClienteSaving] = useState(false);
  const [loadingClienteSelectId, setLoadingClienteSelectId] = useState(null);
  const [loadingClienteRemoveId, setLoadingClienteRemoveId] = useState(null);
  const [showClientesModal, setShowClientesModal] = useState(false);
  const clienteFormRef = useRef(null);
  const nomeClienteLookupRef = useRef('');
  const {
    tabelas,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = useDepositoTabelaPrecoSelection(show);
  const {
    clientes,
    loading: clientesLoading,
    paginaAtual: clientesPaginaAtual,
    quantidadePorPagina: clientesQuantidadePorPagina,
    totalRegistros: clientesTotalRegistros,
    handleFiltrosChange: handleClientesFiltrosChange,
    handlePaginationChange: handleClientesPaginationChange,
    reload: reloadClientes
  } = useDepositoTabelaPrecoClientes(showClientesModal, editingId);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyFormValues);
      setLoadingEditId(null);
      setApplying(false);
      setIsFormCollapsed(true);
      setClienteEditingId(null);
      setClienteFormInitialValues(emptyTabelaPrecoClienteForm);
      setLoadingClienteSelectId(null);
      setLoadingClienteRemoveId(null);
      setShowClientesModal(false);
      nomeClienteLookupRef.current = '';
    }
  }, [show]);

  useEffect(() => {
    setClienteEditingId(null);
    setClienteFormInitialValues({
      ...emptyTabelaPrecoClienteForm,
      idTabelaPrecoDeposito: editingId || ''
    });
    if (!editingId) {
      setShowClientesModal(false);
    }
  }, [editingId]);

  const handleSelectTabela = (tabela) => {
    const selectedValue = {
      value: String(tabela.id),
      label: tabela.nomeTabelaPrecoDeposito || `Tabela ${tabela.id}`
    };
    onSelect(selectedValue);
    if (onSelectComplete) {
      onSelectComplete(tabela);
    }
    onHide();
  };

  const handleEditTabela = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const response = await getDepositoTabelaPrecoById(id);
      const data = response?.data || response || {};
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data));
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao carregar tabela de preco');
      console.error('Erro ao carregar tabela de preco:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleSelectCliente = useCallback(async (id) => {
    if (!id || loadingClienteSelectId === id) return;

    try {
      setLoadingClienteSelectId(id);
      const data = await getDepositoTabelaPrecoClienteById(id);
      if (!data) {
        notification.warning('Associacao nao encontrada.');
        return;
      }
      setClienteEditingId(data.id);
      setClienteFormInitialValues({
        ...mapClienteApiToFormValues(data),
        idTabelaPrecoDeposito: data.idTabelaPrecoDeposito ?? editingId ?? ''
      });
    } catch (error) {
      notification.error('Erro ao carregar associacao de cliente');
      console.error('Erro ao carregar associacao de cliente:', error);
    } finally {
      setLoadingClienteSelectId(null);
    }
  }, [editingId, loadingClienteSelectId]);

  const handleRemoveCliente = useCallback(async (id) => {
    if (!id || loadingClienteRemoveId === id) return;

    try {
      setLoadingClienteRemoveId(id);
      await deleteDepositoTabelaPrecoCliente(id);
      notification.success('Associacao removida com sucesso!');
      if (clienteEditingId === id) {
        setClienteEditingId(null);
        setClienteFormInitialValues({
          ...emptyTabelaPrecoClienteForm,
          idTabelaPrecoDeposito: editingId || ''
        });
      }
      reloadClientes();
    } catch (error) {
      notification.error('Erro ao remover associacao');
      console.error('Erro ao remover associacao:', error);
    } finally {
      setLoadingClienteRemoveId(null);
    }
  }, [clienteEditingId, editingId, loadingClienteRemoveId, reloadClientes]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    setFormInitialValues(emptyFormValues);
    form.reset(emptyFormValues);
    setIsFormCollapsed(false);
    setShowClientesModal(false);
  }, []);

  const handleClearCliente = useCallback((form) => {
    setClienteEditingId(null);
    const resetValues = {
      ...emptyTabelaPrecoClienteForm,
      idTabelaPrecoDeposito: editingId || ''
    };
    setClienteFormInitialValues(resetValues);
    nomeClienteLookupRef.current = '';
    form.reset(resetValues);
  }, [editingId]);

  const handleBuscarNomeCliente = useCallback(async (rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue : rawValue?.target?.value;
    const cnpj = toDigitsOnly(value || '');
    if (!cnpj || cnpj.length !== 14) return;
    if (nomeClienteLookupRef.current === cnpj) return;

    try {
      const response = await getNomeClientePorCnpj(cnpj);
      const nomeCliente = response?.nomeCliente || response?.data?.nomeCliente || '';

      if (nomeCliente) {
        clienteFormRef.current?.change('nome', nomeCliente);
        nomeClienteLookupRef.current = cnpj;
      }
    } catch (error) {
      console.error('Erro ao buscar nome do cliente:', error);
    }
  }, []);

  const tabelaPrecoClienteFormStructureWithLookup = useMemo(
    () =>
      tabelaPrecoClienteFormStructure.map((row) =>
        row.map((field) => {
          if (field?.name === 'cnpjCliente') {
            return {
              ...field,
              onBlur: handleBuscarNomeCliente
            };
          }
          return field;
        })
      ),
    [handleBuscarNomeCliente]
  );

  const persistTabelaPreco = useCallback(async (values, options = {}) => {
    const { silentSuccess = false } = options;
    const payload = buildPayload(values);
    if (Object.keys(payload).length === 0) {
      notification.warning('Nenhuma alteracao para salvar.');
      return null;
    }

    try {
      let response;

      if (values.id) {
        response = await updateDepositoTabelaPreco(values.id, payload);
        if (!silentSuccess) {
          notification.success('Tabela de preco atualizada com sucesso!');
        }
      } else {
        response = await createDepositoTabelaPreco(payload);
        if (!silentSuccess) {
          notification.success('Tabela de preco criada com sucesso!');
        }
      }

      const data = response?.data || response || {};
      setEditingId(data.id || values.id || null);
      setFormInitialValues(mapApiToFormValues(data));
      reload();
      setIsFormCollapsed(false);
      return data;
    } catch (error) {
      notification.error('Erro ao salvar tabela de preco');
      console.error('Erro ao salvar tabela de preco:', error);
      return null;
    }
  }, [reload]);

  const handleSubmit = useCallback(async (values) => {
    try {
      setSaving(true);
      await persistTabelaPreco(values);
    } finally {
      setSaving(false);
    }
  }, [persistTabelaPreco]);

  const handleApply = useCallback(async (values, { invalid, pristine }) => {
    if (!editingId && !values.id) {
      notification.warning('Salve a tabela antes de aplicar.');
      return;
    }

    if (!pristine && invalid) {
      notification.warning('Corrija os campos obrigatorios antes de aplicar.');
      return;
    }

    try {
      setApplying(true);

      let tabelaId = values.id || editingId;
      if (!pristine) {
        const data = await persistTabelaPreco(values, { silentSuccess: true });
        if (!data?.id) {
          return;
        }
        tabelaId = data.id;
      }

      const response = await applyDepositoTabelaPreco(tabelaId);
      const data = response?.data || response || {};

      reload();

      if ((data.quantidadeNotasAtualizadas || 0) > 0) {
        notification.success(`Tabela aplicada com sucesso! ${data.quantidadeNotasAtualizadas} nota(s) atualizada(s).`);
      } else {
        notification.warning('Nenhuma nota elegivel encontrada para aplicar a tabela.');
      }
    } catch (error) {
      notification.error('Erro ao aplicar tabela de preco');
      console.error('Erro ao aplicar tabela de preco:', error);
    } finally {
      setApplying(false);
    }
  }, [editingId, persistTabelaPreco, reload]);

  const handleSubmitCliente = useCallback(async (values, form) => {
    const tabelaPrecoId = editingId || values.idTabelaPrecoDeposito;
    if (!tabelaPrecoId) {
      notification.warning('Selecione uma tabela de preco para vincular clientes.');
      return;
    }

    const payload = buildClientePayload(values, tabelaPrecoId, Boolean(values.id));
    if (Object.keys(payload).length === 0) {
      notification.warning('Nenhuma alteracao para salvar.');
      return;
    }

    try {
      setClienteSaving(true);
      let response;

      if (values.id) {
        response = await updateDepositoTabelaPrecoCliente(values.id, payload);
        notification.success('Associacao atualizada com sucesso!');
      } else {
        response = await createDepositoTabelaPrecoCliente(payload);
        notification.success('Associacao criada com sucesso!');
      }

      const data = response?.data || response || {};
      setClienteEditingId(data.id || values.id || null);
      setClienteFormInitialValues({
        ...mapClienteApiToFormValues(data),
        idTabelaPrecoDeposito: data.idTabelaPrecoDeposito ?? tabelaPrecoId
      });
      reloadClientes();
    } catch (error) {
      notification.error('Erro ao salvar associacao');
      console.error('Erro ao salvar associacao:', error);
    } finally {
      setClienteSaving(false);
    }
  }, [editingId, reloadClientes]);

  const rows = Array.isArray(tabelas) ? tabelas.map(toRowModel) : [];
  const clientesRows = Array.isArray(clientes)
    ? clientes.map((item) => ({
        id: item.id,
        nome: item.nome,
        cnpjCliente: item.cnpjCliente,
        tipoOperacao: item.tipoOperacao,
        dataCriacao: item.dataCriacao,
        dataAtualizacao: item.dataAtualizacao
      }))
    : [];
  const formKey = useMemo(() => (editingId ? `edit-${editingId}` : 'create'), [editingId]);
  const clienteFormKey = useMemo(
    () => (clienteEditingId ? `cliente-${clienteEditingId}` : 'cliente-create'),
    [clienteEditingId]
  );
  const clientesDisabled = !editingId;
  const clientesButtonStyle = clientesDisabled
    ? { backgroundColor: 'transparent', borderColor: '#c9ced6', color: '#8a93a1' }
    : undefined;
  const tabelaContexto = editingId
    ? `${formInitialValues.nomeTabelaPrecoDeposito || 'Tabela'} #${editingId}`
    : null;

  return (
    <>
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Tabela de Preco</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={tabelaPrecoFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

      
        <TanStackTable
          key={`deposito-tabela-preco-${paginaAtual}-${quantidadePorPagina}`}
          columns={getTabelaPrecoColumns(handleSelectTabela, handleEditTabela, loadingEditId)}
          data={rows}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
          enableExportExcel={true}
          exportFileName="deposito-tabelas-preco"
        />

<CollapsibleCard
          className="mt-3"
          title="Formulario da Tabela de Preco"
          isCollapsed={isFormCollapsed}
          onToggle={setIsFormCollapsed}
        >
            <Form
              key={formKey}
              onSubmit={(values, form) => handleSubmit(values, form)}
              initialValues={formInitialValues}
              validate={validateTabelaPrecoForm}
            >
              {({ handleSubmit, form, invalid, pristine, values }) => (
                <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm
                    structure={tabelaPrecoFormStructure}
                    sections={tabelaPrecoFormSections()}
                    readOnly={false}
                  />

                <FormButtonToolbar className="mt-3">
                  <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                    Limpar
                  </Button>
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={() => setShowClientesModal(true)}
                    disabled={clientesDisabled}
                    style={clientesButtonStyle}
                  >
                    Clientes
                  </Button>
                  <ButtonWithLoading
                    variant="outline-primary"
                    type="button"
                    onClick={() => handleApply(values, { invalid, pristine })}
                    loading={applying}
                    disabled={!editingId || saving || applying || !!loadingEditId}
                  >
                    Aplicar
                  </ButtonWithLoading>
                  <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                    Salvar
                  </ButtonWithLoading>
                </FormButtonToolbar>
                </FormContainer>
              )}
            </Form>
        </CollapsibleCard>


      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {rows.length} de {totalRegistros} tabela(s)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
    <Modal
      show={showClientesModal}
      onHide={() => setShowClientesModal(false)}
      animation={false}
      size="xl"
      dialogClassName="modal-uniform-xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Clientes da Tabela de Preco{tabelaContexto ? ` - ${tabelaContexto}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={tabelaPrecoClienteFilterConfig}
          onApply={handleClientesFiltrosChange}
          title="Filtros de Clientes"
          loading={clientesLoading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`deposito-tabela-preco-clientes-${clientesPaginaAtual}-${clientesQuantidadePorPagina}`}
          columns={getTabelaPrecoClienteColumns(
            handleSelectCliente,
            handleRemoveCliente,
            loadingClienteSelectId,
            loadingClienteRemoveId
          )}
          data={clientesRows}
          loading={clientesLoading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={clientesTotalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (clientesPaginaAtual || 1) - 1),
            pageSize: clientesQuantidadePorPagina || 10
          }}
          onPaginationChange={handleClientesPaginationChange}
        />

        <Form
          key={clienteFormKey}
          onSubmit={(values, form) => handleSubmitCliente(values, form)}
          initialValues={clienteFormInitialValues}
        >
        {({ handleSubmit, form }) => {
          clienteFormRef.current = form;

          return (
            <FormContainer onSubmit={handleSubmit} className="mt-3">
              <DynamicForm
                structure={tabelaPrecoClienteFormStructureWithLookup}
                sections={tabelaPrecoClienteFormSections()}
                readOnly={false}
              />

              <FormButtonToolbar className="mt-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => handleClearCliente(form)}
                  disabled={clienteSaving}
                >
                  Limpar
                </Button>
                <ButtonWithLoading variant="primary" type="submit" loading={clienteSaving}>
                  Salvar
                </ButtonWithLoading>
              </FormButtonToolbar>
            </FormContainer>
          );
        }}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {clientesTotalRegistros > 0 && (
              <span>
                Exibindo {clientesRows.length} de {clientesTotalRegistros} cliente(s)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={() => setShowClientesModal(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default DepositoTabelaPrecoSelectionModal;
